import React from "react";
import { Paper, Box, Typography, useTheme, alpha } from "@mui/material";
import { useTranslation } from "react-i18next";

const StatsCard = ({ label, count, icon, breakdown, variant }) => {
  const theme = useTheme();

  const isLight = theme.palette.mode === "light";

  // Enhanced glassmorphism background
  const glassBg = isLight ? "rgba(255,255,255,0.90)" : "rgba(34,45,59,0.85)";

  // Dynamic shadow
  const boxShadow = isLight
    ? "0 4px 24px 0 rgba(2,89,165,0.10), 0 1.5px 8px 0 rgba(0,0,0,0.03)"
    : "0 4px 24px 0 rgba(0,188,212,0.07), 0 1.5px 8px 0 rgba(0,0,0,0.25)";

  // Modern gradient icon background
  const iconBg = isLight
    ? `linear-gradient(135deg, ${alpha(
        theme.palette.primary.main,
        0.1,
      )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
    : `linear-gradient(135deg, ${alpha(
        theme.palette.primary.main,
        0.2,
      )} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`;

  // Border color
  const borderColor = isLight ? "rgba(2,89,165,0.08)" : "rgba(0,188,212,0.18)";

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: { xs: 3, md: 5 },
        display: "flex",
        flexDirection: "column",
        background: glassBg,
        height: { xs: 240, sm: 260, md: 280 },
        boxShadow: boxShadow,
        border: `1px solid ${borderColor}`,
        backdropFilter: "blur(12px)",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: isLight
            ? "0 12px 48px 0 rgba(2,89,165,0.15), 0 4px 20px 0 rgba(0,0,0,0.08)"
            : "0 12px 48px 0 rgba(0,188,212,0.18), 0 4px 20px 0 rgba(0,0,0,0.35)",
          transform: "translateY(-4px)",
          borderColor: isLight
            ? alpha(theme.palette.primary.main, 0.2)
            : alpha(theme.palette.primary.main, 0.3),
        },
      }}
    >
      {/* Header Row: Icon + Label on left, Count on right */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2.5,
        }}
      >
        {/* Left side: Icon and Label */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, md: 2 },
          }}
        >
          <Box
            sx={{
              background: iconBg,
              borderRadius: { xs: 2, md: 3 },
              width: { xs: 44, sm: 50, md: 56 },
              height: { xs: 44, sm: 50, md: 56 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            {React.isValidElement(icon)
              ? React.cloneElement(icon, {
                  sx: {
                    fontSize: { xs: 28, md: 34 },
                    color: theme.palette.primary.main,
                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                    ...icon.props.sx,
                  },
                })
              : icon}
          </Box>

          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 700,
              lineHeight: 1.2,
              textTransform: "uppercase",
              opacity: 0.75,
              fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
              letterSpacing: 0.1,
            }}
          >
            {label}
          </Typography>
        </Box>

        {/* Right side: Count */}
        <Typography
          variant="h2"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 800,
            lineHeight: 1,
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            letterSpacing: "-1px",
            background: isLight
              ? `linear-gradient(135deg, ${
                  theme.palette.text.primary
                } 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`
              : `linear-gradient(135deg, ${
                  theme.palette.text.primary
                } 0%, ${alpha(theme.palette.primary.light, 0.9)} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {count}
        </Typography>
      </Box>

      {/* Breakdown / extras */}
      {variant === "users" &&
        breakdown &&
        Object.keys(breakdown).length > 0 && (
          <UsersBreakdown
            breakdown={breakdown}
            theme={theme}
            isLight={isLight}
          />
        )}
      {variant !== "users" &&
        breakdown &&
        Object.keys(breakdown || {}).length > 0 && (
          <ModernBreakdown
            breakdown={breakdown}
            theme={theme}
            isLight={isLight}
          />
        )}
    </Paper>
  );
};

// Modern breakdown for Users card with visual bar
const UsersBreakdown = ({ breakdown, theme, isLight }) => {
  const { t } = useTranslation();
  const order = ["user", "informant", "journalist"];
  const entries = order
    .filter((k) => breakdown[k] !== undefined)
    .map((k) => [k, breakdown[k]]);
  const total = entries.reduce((acc, [, v]) => acc + (Number(v) || 0), 0);

  const colorMap = {
    user: theme.palette.primary.main,
    informant: theme.palette.success.main,
    journalist: theme.palette.secondary.main,
  };

  return (
    <Box sx={{ mt: 1 }}>
      {/* Progress bars for each type */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {entries.map(([key, value]) => {
          const percentage = total > 0 ? (Number(value) * 100) / total : 0;
          const color = colorMap[key] || theme.palette.text.disabled;

          return (
            <Box key={key}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                    textTransform: "capitalize",
                  }}
                >
                  {t(`dashboard.userTypes.${key}`)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: color,
                    fontWeight: 700,
                    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                  }}
                >
                  {value}
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "relative",
                  height: 6,
                  borderRadius: 999,
                  backgroundColor: isLight
                    ? alpha(color, 0.1)
                    : alpha(color, 0.15),
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: `${percentage}%`,
                    background: `linear-gradient(90deg, ${color}, ${alpha(
                      color,
                      0.8,
                    )})`,
                    borderRadius: 999,
                    transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Modern breakdown for Topics and other cards - with progress bars like Users
const ModernBreakdown = ({ breakdown, theme, isLight }) => {
  const entries = Object.entries(breakdown);
  const total = entries.reduce((acc, [, v]) => acc + (Number(v) || 0), 0);

  const colorMap = {
    "With Contributions": theme.palette.success.main,
    "Open to Contribute": theme.palette.warning.main,
    "Active Discussions": theme.palette.success.main,
    "Pending Discussions": theme.palette.warning.main,
    "Pending Response": theme.palette.warning.main,
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {entries.map(([key, value]) => {
          const percentage = total > 0 ? (Number(value) * 100) / total : 0;
          const color = colorMap[key] || theme.palette.primary.main;

          return (
            <Box key={key}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                  }}
                >
                  {key}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: color,
                    fontWeight: 700,
                    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                  }}
                >
                  {value}
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "relative",
                  height: 6,
                  borderRadius: 999,
                  backgroundColor: isLight
                    ? alpha(color, 0.1)
                    : alpha(color, 0.15),
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: `${percentage}%`,
                    background: `linear-gradient(90deg, ${color}, ${alpha(
                      color,
                      0.8,
                    )})`,
                    borderRadius: 999,
                    transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default StatsCard;
